import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import useExperiencesSortOptions from '../hooks/useExperiencesSortOptions'
import Group from 'components/utils/Group'
import useExperiencesSearchFilters from '../hooks/useExperiencesSearchFilters'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ExperienceDenseSearchList from 'components/Experiences/ExperienceLists/ExperienceDenseSearchList'
import ExperienceSearchNoResults from './ExperienceSearchNoResults'

const Loader = styled(LoadingIndicator)`
  padding: 0;
`

const ListContent = styled(VerticalSpacer)`
  margin-top: 0 !important;
  padding-top: ${rem(40)};
`

interface Props {
  list: App.ApiCallState<{
    ids: Array<string>
    placeId: string
  }>
  destinationName: string
  sortBy?: Experiences.SortTypes
  excludeIds?: Array<string>
}

function ExperienceSearchPageList(props: Props) {
  const intl = useIntl()
  const { sortBy } = useExperiencesSortOptions()
  const { list, excludeIds } = props
  const fetching = !list || list.fetching

  const { searchFilters } = useExperiencesSearchFilters()
  const validItemsLength = useMemo(() => list.ids.filter(id => !excludeIds?.includes(id)).length, [list.ids, excludeIds])
  const isNoResults = !fetching && validItemsLength === 0
  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', 'Experiences Page Offer List'),
    { key: list.placeId },
  )

  return (
    <ListContent gap={16}>
      {!isNoResults && (
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
          <Group direction="horizontal" verticalAlign="center">
            {!fetching && list && <BodyText variant="medium" colour="neutral-two">{intl.formatMessage(
              {
                id: 'tXFmCPud-a05Cl708Hm6',
                defaultMessage: '{count, plural, one {# experience} other {# experiences found}}',
                description: 'Pluralized label showing the number of experiences returned in search results',
              },
              { count: validItemsLength },
            )}
            </BodyText>}
            {fetching && <Loader inline />}
          </Group>
          <CSSBreakpoint min="tablet">
            <ExperienceSearchPageSort />
          </CSSBreakpoint>
        </Group>
      )}
      {isNoResults && <ExperienceSearchNoResults
        placeId={list.placeId}
        destinationName={props.destinationName}
      />}
      {!isNoResults && <OfferListEventsProvider tracking={tracking}>
        <ExperienceDenseSearchList
          placeId={list.placeId}
          sortBy={sortBy}
          categoryCodes={searchFilters.categoryCodes}
          priceGte={searchFilters.priceGte}
          priceLte={searchFilters.priceLte}
          luxPlusFeatures={searchFilters.luxPlusFeatures}
          excludeIds={excludeIds}
          collections={searchFilters.collections}
          from={searchFilters.checkIn}
          to={searchFilters.checkOut}
          minRating={searchFilters.minRating}
          leExclusive={searchFilters.leExclusive}
          travelStyles={searchFilters.travelStyles}
          featured={searchFilters.featured}
        />
      </OfferListEventsProvider>}
    </ListContent>
  )
}

export default ExperienceSearchPageList
